import React, { useState } from 'react';
import {
  Image, Linking, ScrollView, StyleSheet, TouchableOpacity, View,
} from 'react-native';
import Constants from 'expo-constants';
import {
  ActivityIndicator, Appbar, Button, Card, Checkbox, Chip, DataTable, Dialog, Divider, List,
  Portal, Text, TextInput,
} from 'react-native-paper';
import { useMutation, useQuery } from '@apollo/client';
import { NativeBaseProvider } from 'native-base';
import { useNavigation } from '@react-navigation/native';
import { useToast } from 'react-native-toast-notifications';
import { SCAN_NAS_FOLDER, CONFIRM_NAS_MEDIA_SUGGESTIONS } from '../../gql/nas/mutation';
import { NAS_ASSIGNABLE_USERS, NAS_FILES_FOR_ADMIN } from '../../gql/nas/query';

const MATCH_LABELS = {
  exact: { label: 'Exact', color: '#e8f5e9', textColor: '#2e7d32' },
  partial: { label: 'Partiel', color: '#fff8e1', textColor: '#f57f17' },
  none: { label: 'Aucun', color: '#f5f5f5', textColor: '#757575' },
};

// The API returns relative media links; they must be loaded from the API host, not the front end's.
const API_URL = Constants.expoConfig?.extra?.EXPO_PUBLIC_API_URL ?? '';
const apiMediaUrl = (path) => (path ? `${API_URL}${path}` : null);

const VIDEO_EXT = /\.(mp4|m4v|mov|webm|mkv|avi)$/i;
const isVideoFile = (s) => (s.mime ?? '').startsWith('video/') || VIDEO_EXT.test(s.displayName);

const THUMB_SIZE = 56;
const thumbStyle = {
  width: THUMB_SIZE,
  height: THUMB_SIZE,
  borderRadius: 4,
  backgroundColor: '#eee',
};

// Falls back to an icon when DSM has no thumbnail (unsupported type, video not indexed yet).
// eslint-disable-next-line react/prop-types
function Thumbnail({ uri, isVideo, onPress }) {
  const [failed, setFailed] = useState(false);
  return (
    <TouchableOpacity onPress={onPress}>
      {!uri || failed ? (
        <View style={[thumbStyle, { alignItems: 'center', justifyContent: 'center' }]}>
          <List.Icon icon={isVideo ? 'video' : 'file-question-outline'} />
        </View>
      ) : (
        <View>
          <Image
            source={{ uri }}
            onError={() => setFailed(true)}
            style={thumbStyle}
            resizeMode="cover"
          />
          {isVideo && (
            <View style={[StyleSheet.absoluteFill, { alignItems: 'center', justifyContent: 'center' }]}>
              <List.Icon icon="play-circle" color="#fff" />
            </View>
          )}
        </View>
      )}
    </TouchableOpacity>
  );
}

function renderFolderIcon(props) {
  // eslint-disable-next-line react/jsx-props-no-spreading
  return <List.Icon {...props} icon="folder" />;
}

function NasScan() {
  const toast = useToast();
  const navigation = useNavigation();

  const [path, setPath] = useState('');
  // Folder currently shown in the browser. null = server default (the configured basePath).
  const [browsePath, setBrowsePath] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  // Path of the last completed scan, so an empty result can be told apart from "not scanned yet"
  const [scannedPath, setScannedPath] = useState(null);
  // selected: Set of indices that are checked for confirmation
  const [selected, setSelected] = useState(new Set());
  // overrides: { [index]: { id, name } } — admin picked a user (or changed the suggested one)
  const [overrides, setOverrides] = useState({});
  // Row index whose user is being picked, null when the picker is closed
  const [pickerRow, setPickerRow] = useState(null);
  const [userSearch, setUserSearch] = useState('');

  const { data: usersData } = useQuery(NAS_ASSIGNABLE_USERS);

  const { data: browseData, loading: browsing, error: browseError } = useQuery(
    NAS_FILES_FOR_ADMIN,
    { variables: { path: browsePath }, fetchPolicy: 'network-only' },
  );
  const folders = (browseData?.nasFilesForAdmin ?? []).filter((item) => item.type === 'directory');
  const fileCount = (browseData?.nasFilesForAdmin ?? []).length - folders.length;

  const openFolder = (folderPath) => { setBrowsePath(folderPath); setPath(folderPath); };
  const goUp = () => {
    const parent = (browsePath ?? '').replace(/\/[^/]*\/?$/, '') || '/';
    openFolder(parent);
  };

  const [scanFolder, { loading: scanning }] = useMutation(SCAN_NAS_FOLDER, {
    onCompleted: (d) => {
      const results = d.scanNasFolder;
      setSuggestions(results);
      setScannedPath(path);
      // Pre-select rows that have a match
      const preSelected = new Set(
        results.reduce((acc, s, i) => (s.matchType !== 'none' ? [...acc, i] : acc), []),
      );
      setSelected(preSelected);
      setOverrides({});
    },
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const [confirmSuggestions, { loading: confirming }] = useMutation(CONFIRM_NAS_MEDIA_SUGGESTIONS, {
    onCompleted: (d) => {
      toast.show(`${d.confirmNasMediaSuggestions.attached} médias attachés avec succès`);
      setSuggestions([]);
      setSelected(new Set());
    },
    onError: (e) => toast.show(e.message, { type: 'danger' }),
  });

  const pickerUsers = (usersData?.users ?? []).filter((u) => (
    `${u.firstName} ${u.lastName}`.toLowerCase().includes(userSearch.trim().toLowerCase())
  ));

  const pickUser = (user) => {
    setOverrides((prev) => ({ ...prev, [pickerRow]: { id: user.id, name: `${user.firstName} ${user.lastName}` } }));
    setSelected((prev) => new Set(prev).add(pickerRow));
    setPickerRow(null);
    setUserSearch('');
  };

  // Hand the file to the system: a new browser tab on web, the device's viewer/player on mobile.
  const openPreview = (file) => {
    Linking.openURL(apiMediaUrl(file.previewUrl)).catch(() => (
      toast.show("Impossible d'ouvrir le fichier", { type: 'danger' })
    ));
  };

  const toggleRow = (i) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i); else next.add(i);
      return next;
    });
  };

  const handleConfirm = () => {
    const toAttach = [...selected].map((i) => {
      const s = suggestions[i];
      const userId = overrides[i]?.id ?? s.suggestedUserId;
      return { nasPath: s.nasPath, userId, displayName: s.displayName };
    }).filter((s) => s.userId != null);

    if (toAttach.length === 0) { toast.show('Aucune ligne valide sélectionnée'); return; }
    confirmSuggestions({ variables: { suggestions: toAttach } });
  };

  return (
    <View style={{ flex: 1, backgroundColor: '#fff' }}>
      <Appbar.Header>
        <Appbar.BackAction onPress={() => navigation.goBack()} />
        <Appbar.Content title="Scanner un dossier NAS" />
      </Appbar.Header>

      <ScrollView contentContainerStyle={{ padding: 16, gap: 12 }}>
        <Card>
          <Card.Content style={{ gap: 8 }}>
            <TextInput
              label="Chemin du dossier à scanner (ex: /Public/Compétition/2024)"
              value={path}
              onChangeText={setPath}
              onSubmitEditing={() => path.trim() && setBrowsePath(path.trim())}
              autoCapitalize="none"
            />
            <Divider />
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
              <Button icon="arrow-up" compact onPress={goUp} disabled={browsePath == null || browsePath === '/'}>
                Dossier parent
              </Button>
              <Text variant="bodySmall" style={{ opacity: 0.6, flex: 1 }} numberOfLines={1}>
                {`${browsePath ?? 'Dossier de base'} — ${fileCount} fichier(s)`}
              </Text>
              {browsing && <ActivityIndicator size="small" />}
            </View>
            {browseError && (
              <Text variant="bodySmall" style={{ color: '#c62828' }}>{browseError.message}</Text>
            )}
            <ScrollView style={{ maxHeight: 240 }} nestedScrollEnabled>
              {folders.map((f) => (
                <List.Item
                  key={f.path}
                  title={f.name}
                  left={renderFolderIcon}
                  onPress={() => openFolder(f.path)}
                />
              ))}
              {!browsing && !browseError && folders.length === 0 && (
                <Text variant="bodySmall" style={{ opacity: 0.6, padding: 8 }}>Aucun sous-dossier</Text>
              )}
            </ScrollView>
          </Card.Content>
          <Card.Actions>
            <Button
              mode="contained"
              onPress={() => scanFolder({ variables: { path } })}
              loading={scanning}
              disabled={!path.trim()}
              icon="folder-search"
            >
              Scanner
            </Button>
          </Card.Actions>
        </Card>

        {scannedPath != null && suggestions.length === 0 && (
          <Text variant="bodyMedium" style={{ opacity: 0.7 }}>
            {`Aucun fichier trouvé dans ${scannedPath} ni dans ses sous-dossiers.`}
          </Text>
        )}

        {suggestions.length > 0 && (
          <>
            <Text variant="titleMedium">
              {suggestions.length} fichier(s) trouvé(s) — {selected.size} sélectionné(s)
            </Text>

            <DataTable>
              <DataTable.Header>
                <DataTable.Title style={{ flex: 0.3 }}> </DataTable.Title>
                <DataTable.Title style={{ flex: 0.9 }}>Aperçu</DataTable.Title>
                <DataTable.Title style={{ flex: 1.8 }}>Fichier</DataTable.Title>
                <DataTable.Title style={{ flex: 1.5 }}>Utilisateur suggéré</DataTable.Title>
                <DataTable.Title style={{ flex: 0.7 }}>Match</DataTable.Title>
              </DataTable.Header>

              {suggestions.map((s, i) => {
                const matchStyle = MATCH_LABELS[s.matchType] ?? MATCH_LABELS.none;
                const userId = overrides[i]?.id ?? s.suggestedUserId;
                let userName = 'Choisir…';
                if (overrides[i]) userName = overrides[i].name;
                else if (userId) userName = `${s.suggestedUserFirstName} ${s.suggestedUserLastName}`;

                return (
                  <DataTable.Row
                    key={s.nasPath}
                    onPress={() => toggleRow(i)}
                    style={selected.has(i) ? { backgroundColor: '#f3f8ff' } : undefined}
                  >
                    <DataTable.Cell style={{ flex: 0.3 }}>
                      <Checkbox
                        status={selected.has(i) ? 'checked' : 'unchecked'}
                        onPress={() => toggleRow(i)}
                        disabled={!userId}
                      />
                    </DataTable.Cell>
                    <DataTable.Cell style={{ flex: 0.9 }}>
                      <Thumbnail
                        uri={apiMediaUrl(s.thumbnailUrl)}
                        isVideo={isVideoFile(s)}
                        onPress={() => openPreview(s)}
                      />
                    </DataTable.Cell>
                    <DataTable.Cell style={{ flex: 1.8 }}>
                      <View>
                        <Text variant="bodySmall" numberOfLines={2}>{s.displayName}</Text>
                        <Text variant="labelSmall" style={{ opacity: 0.5 }} numberOfLines={1}>
                          {s.nasPath.slice(0, s.nasPath.lastIndexOf('/')) || '/'}
                        </Text>
                      </View>
                    </DataTable.Cell>
                    <DataTable.Cell style={{ flex: 1.5 }} onPress={() => setPickerRow(i)}>
                      <Text variant="bodySmall" style={{ textDecorationLine: 'underline' }}>
                        {userName}
                      </Text>
                    </DataTable.Cell>
                    <DataTable.Cell style={{ flex: 0.7 }}>
                      <Chip
                        compact
                        style={{ backgroundColor: matchStyle.color }}
                        textStyle={{ color: matchStyle.textColor, fontSize: 10 }}
                      >
                        {matchStyle.label}
                      </Chip>
                    </DataTable.Cell>
                  </DataTable.Row>
                );
              })}
            </DataTable>

            <Divider />
            <Button
              mode="contained"
              onPress={handleConfirm}
              loading={confirming}
              disabled={selected.size === 0}
              icon="check-all"
            >
              Confirmer la sélection ({selected.size})
            </Button>
          </>
        )}
      </ScrollView>

      <Portal>
        <Dialog visible={pickerRow != null} onDismiss={() => setPickerRow(null)}>
          <Dialog.Title>Attribuer à un utilisateur</Dialog.Title>
          <Dialog.Content style={{ gap: 8 }}>
            <TextInput
              label="Rechercher un utilisateur"
              value={userSearch}
              onChangeText={setUserSearch}
              autoCapitalize="none"
              dense
            />
            <ScrollView style={{ maxHeight: 280 }} keyboardShouldPersistTaps="handled">
              {pickerUsers.map((u) => (
                <List.Item
                  key={u.id}
                  title={`${u.firstName} ${u.lastName}`}
                  onPress={() => pickUser(u)}
                />
              ))}
              {pickerUsers.length === 0 && (
                <Text variant="bodySmall" style={{ opacity: 0.6, padding: 8 }}>Aucun utilisateur</Text>
              )}
            </ScrollView>
          </Dialog.Content>
          <Dialog.Actions>
            <Button onPress={() => setPickerRow(null)}>Annuler</Button>
          </Dialog.Actions>
        </Dialog>
      </Portal>
    </View>
  );
}

export default function NasScanScreen() {
  return (
    <NativeBaseProvider>
      <NasScan />
    </NativeBaseProvider>
  );
}
